import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle, Circle } from 'lucide-react';
import {
  ModelScopeFactory,
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { Chip, Text, Tooltip } from '@cotera/client/app/components/ui';
import { ArchivedNotice } from '../resources/archived-notice';
import { WorkflowEditorLoading } from './workflow-editor-loading';
import { useBlockNavigationWithModal } from '@cotera/client/app/hooks/use-history-blocker';
import { WorkflowEditorCanvas } from './workflow-editor-canvas';
import { WorkflowEditorFloatingChrome } from './workflow-editor-floating-chrome';
import { WorkflowEditorRunsPanel } from './workflow-editor-runs-panel';
import { useWorkflowAutosave } from './use-workflow-autosave';
import { useHealLegacyBindingSources } from './hooks/use-heal-legacy-binding-sources';
import {
  useActiveArtifactId,
  ActiveArtifactIdOverride,
  useArtifactPanel,
  type ArtifactPanelViewEntry,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { WorkflowRunStackContent } from './workflow-run-detail.page';
import {
  WORKFLOW_RUN_VIEW_TYPE,
  getWorkflowDrillTargetTitle,
  isWorkflowAgentViewPayload,
  isWorkflowDrillViewPayload,
  isWorkflowDatasetViewPayload,
  isWorkflowGraphViewPayload,
  isWorkflowRunViewPayload,
  isWorkflowRunStepViewPayload,
  isWorkflowTreeViewPayload,
  WORKFLOW_TREE_VIEW_TITLE,
  viewOwnsPageChrome,
  type WorkflowRunStepViewPayload,
} from './workflow-artifact-views';
import {
  WorkflowAgentStackContent,
  WorkflowDatasetStackContent,
  WorkflowDrillStackContent,
} from './workflow-node-stack-view';
import { WorkflowRunStepStackContent } from './workflow-run-step-stack-view';
import { WorkflowNestedTreeStackContent } from './workflow-nested-tree-stack-view';
import { WorkflowRunsList } from './workflow-runs-list';
import { useRunUrlSync } from './use-run-url-sync';
import { runDetailsCrumbLabel } from './workflow-breadcrumbs';
import type { WorkflowRun } from './workflow-run.types';
import { WorkflowRunModel } from './workflow-run.model';
import { WorkflowRunsModel } from './workflow-runs.model';
import { WorkflowRunActionTargetsModel } from './workflow-run-action-targets.model';
import {
  ResourcePageShell,
  ResourcePageShellBackButton,
  useResourceBreadcrumbs,
} from '../resources/resource-page-shell';
import {
  WorkflowModel,
  WorkflowModelProvider,
  useWorkflowModel,
  type WorkflowEditorTab,
  type WorkflowSeedNewDatasetTrigger,
} from './workflow.model';
import { ResourceType } from '@cotera/api';
import { FavoriteToggleButton } from '../resources/favorites';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import {
  chatSeedKeysToClearOnPersist,
  openArtifactChatSeed,
} from '../chat-seed';
import { workflowChatSeedStrategy } from './chat-seed';
import { SaveWorkflowAction } from './save-workflow.action';
import type { WorkflowTriggerOption } from '../trigger/hooks';
import { WorkflowRenderCatalogProvider } from './workflow-render-catalog';
import { registerArchiveDetailPageBeforeNavigate } from '../resources/actions/archive-resource.action';
import { registerWorkflowSeedRuntime } from './chat-seed/runtime';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';

type WorkflowPageLocationState = {
  new?: boolean;
  folderId?: string;
} | null;

/** The payload of whatever view is on top of the stack, if any. */
function topStackPayload(
  viewStack: readonly ArtifactPanelViewEntry[] | undefined
): unknown {
  if (viewStack === undefined || viewStack.length === 0) {
    return undefined;
  }
  return viewStack[viewStack.length - 1]?.payload;
}

/**
 * The top-of-stack payload, when it is the kind of view `isPayload` recognises.
 *
 * The page decides what to render from *which* view is on top, so it asks that
 * question once per view type. Written seven times over as the same memo, the
 * only thing that varied was the guard — so the guard is the parameter, and the
 * eighth view type costs a line rather than a block.
 */
function useActiveStackView<T>(
  viewStack: readonly ArtifactPanelViewEntry[] | undefined,
  isPayload: (payload: unknown) => payload is T
): T | null {
  return useMemo(() => {
    const payload = topStackPayload(viewStack);
    return isPayload(payload) ? payload : null;
    // `isPayload` is a module-level guard, stable by construction; listing it
    // would only invite an inline arrow at a call site to defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStack]);
}

/**
 * Targets the workflow model only when this workflow is the active artifact
 * *and* we are not drilled into an agent stack view. While an agent is open on
 * the stack, the agent owns Ctrl+S (see ActiveArtifactIdOverride in
 * WorkflowAgentStackContent).
 */
const WorkflowModelStackAwareProvider: React.FC<{
  modelId: string;
  baseTarget: boolean;
  children: React.ReactNode;
}> = ({ modelId, baseTarget, children }) => {
  const artifactPanel = useArtifactPanel();
  const top = topStackPayload(artifactPanel?.viewStack);
  const agentOnStack = isWorkflowAgentViewPayload(top);
  const graphOnStack = isWorkflowGraphViewPayload(top) ? top : null;
  const target =
    baseTarget &&
    !agentOnStack &&
    (graphOnStack === null || graphOnStack.targetWorkflowId === modelId);

  return (
    <WorkflowModelProvider modelId={modelId} target={target}>
      {children}
    </WorkflowModelProvider>
  );
};

const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave?';

// The runs list/detail/step views render in this embedded panel (not just on
// their standalone routes), so each scopes the run view model it reads.
const EmbeddedRunsList: React.FC<{
  workflowId: string;
  onClickRun?: (run: WorkflowRun) => void;
  onOpenRunId?: (runId: string) => void;
}> = ({ workflowId, onClickRun, onOpenRunId }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org?.id) ?? '';
  const createModels = useCallback(
    () => [
      new WorkflowRunsModel({ workflowId, orgId, client, queryClient }),
      // Carries the rows the cancel/retry actions act on; the list keeps it in
      // step with its selection.
      new WorkflowRunActionTargetsModel({ workflowId }),
    ],
    [workflowId, orgId, client, queryClient]
  );
  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[workflowId, orgId]}
      key={`runs:${workflowId}`}
    >
      <WorkflowRunsList
        workflowId={workflowId}
        layout="panel"
        onClickRun={onClickRun}
        onOpenRunId={onOpenRunId}
      />
    </ModelScopeFactory>
  );
};

const EmbeddedRunStep: React.FC<{ view: WorkflowRunStepViewPayload }> = ({
  view,
}) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org?.id) ?? '';
  const createModels = useCallback(
    () => [
      new WorkflowRunModel({
        workflowId: view.workflowId,
        runId: view.runId,
        orgId,
        client,
        queryClient,
      }),
    ],
    [view.workflowId, view.runId, orgId, client, queryClient]
  );
  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[view.workflowId, view.runId, orgId]}
      key={`run-step:${view.runId}`}
    >
      <WorkflowRunStepStackContent view={view} />
    </ModelScopeFactory>
  );
};

const WorkflowPageContent: React.FC<{
  folderIdFromState?: string;
  skipNextBlockRef: React.MutableRefObject<boolean>;
  stackViewsEnabled?: boolean;
}> = ({ folderIdFromState, skipNextBlockRef, stackViewsEnabled = true }) => {
  const model = useWorkflowModel();
  const org = useWhoami((s) => s.org);
  const [searchParams] = useSearchParams();
  const urlAdapter = useUrlAdapter();
  const saveWorkflowAction = useAction(SaveWorkflowAction);
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  const tab = useWatchableValue(model.editorTabWatchable);
  const previousEditorTabRef = useRef(tab);
  const runsPanelOpen = tab === 'runs';
  const name = useWatchableValue(model.nameWatchable);
  const loadStatus = useWatchableValue(model.loadStatusWatchable);
  const resourceSnapshot = useWatchableValue(model.resourceWatchable);
  const hasUnsavedChanges = useWatchableValue(model.hasUnsavedChangesWatchable);

  useHealLegacyBindingSources(model);
  const folderId = resourceSnapshot?.meta?.folderId ?? null;
  const folderScope = resourceSnapshot?.meta?.folderScope ?? null;
  // The resource's *archive* status, not `workflowStatus` (the row's own
  // active/paused field). An archived workflow still opens by id — the builder
  // is the only place it is reachable from — so the page has to say so itself.
  const isArchived = resourceSnapshot?.workflow.status === 'archived';
  const isPersisted = useWatchableValue(model.isNewWatchable) === false;
  const artifactPanel = useArtifactPanel();
  const viewStack = stackViewsEnabled ? artifactPanel?.viewStack : undefined;
  const canPopView = stackViewsEnabled
    ? artifactPanel?.canPopView ?? false
    : false;
  const resetViewStack = stackViewsEnabled
    ? artifactPanel?.resetViewStack
    : undefined;

  const activeRunView = useActiveStackView(viewStack, isWorkflowRunViewPayload);
  const activeDrillView = useActiveStackView(
    viewStack,
    isWorkflowDrillViewPayload
  );
  const activeRunStepView = useActiveStackView(
    viewStack,
    isWorkflowRunStepViewPayload
  );
  const activeAgentView = useActiveStackView(
    viewStack,
    isWorkflowAgentViewPayload
  );
  const activeDatasetView = useActiveStackView(
    viewStack,
    isWorkflowDatasetViewPayload
  );
  const activeGraphView = useActiveStackView(
    viewStack,
    isWorkflowGraphViewPayload
  );
  const activeTreeView = useActiveStackView(
    viewStack,
    isWorkflowTreeViewPayload
  );

  const handleCloseStackView = useCallback(() => {
    artifactPanel?.popView();
  }, [artifactPanel]);

  const openRunInPanel = useCallback(
    (runId: string) => {
      artifactPanel?.pushView(
        {
          id: `workflow-run-${runId}`,
          title: runDetailsCrumbLabel(runId),
          payload: {
            type: WORKFLOW_RUN_VIEW_TYPE,
            workflowId: model.workflowId,
            runId,
          },
        },
        { replaceTop: true }
      );
    },
    [artifactPanel, model.workflowId]
  );

  const handleClickRunInPanel = useCallback(
    (run: WorkflowRun) => {
      openRunInPanel(run.id);
    },
    [openRunInPanel]
  );

  const handleRunsPanelOpenChange = useCallback(
    (open: boolean) => {
      model.setEditorTab(open ? 'runs' : 'graph');
    },
    [model]
  );

  useEffect(() => {
    model.syncEditorTabFromUrl(searchParams);
  }, [model, searchParams]);

  // The run open in the panel and the `?run=<id>` URL param, kept in step in
  // both directions. See useRunUrlSync for why this is not a UrlParamWatchable.
  const leaveRunView = useCallback(() => {
    artifactPanel?.resetViewStack();
  }, [artifactPanel]);

  useRunUrlSync({
    enabled: stackViewsEnabled && artifactPanel !== null,
    searchParams,
    openRunId: activeRunView?.runId,
    openRun: openRunInPanel,
    leaveRunView,
    urlAdapter,
  });

  // A brand-new workflow's chat-seed URL carries `new=1` — "this workflow does
  // not exist on the server yet". Saving it makes that a lie, so drop the marker
  // (and the new-only seed trigger params) as soon as the workflow is persisted:
  // otherwise navigating away and coming back to this URL reopens the new
  // workflow flow with a blank draft instead of the workflow just saved. Written
  // through the url adapter so it replaces the current history entry off the
  // freshest location rather than a stale render's search params.
  useEffect(() => {
    if (!isPersisted) {
      return;
    }
    const keys = chatSeedKeysToClearOnPersist(searchParams, model.workflowId);
    if (keys.length === 0) {
      return;
    }
    // The adapter deletes a key when its value is empty.
    const cleared: Record<string, string> = {};
    for (const key of keys) {
      cleared[key] = '';
    }
    urlAdapter.setParams(cleared);
  }, [isPersisted, searchParams, urlAdapter, model.workflowId]);

  useEffect(() => {
    if (resetViewStack === undefined) {
      return;
    }
    const previousTab = previousEditorTabRef.current;
    previousEditorTabRef.current = tab;
    if (previousTab === tab) {
      return;
    }
    if (viewStack !== undefined && viewStack.length > 0) {
      resetViewStack();
    }
  }, [tab, viewStack, resetViewStack]);

  const handleToolbarSlotChange = useCallback((node: HTMLDivElement | null) => {
    setToolbarSlot(node);
  }, []);

  useEffect(() => {
    registerArchiveDetailPageBeforeNavigate(() => {
      skipNextBlockRef.current = true;
    });
    return () => {
      registerArchiveDetailPageBeforeNavigate(undefined);
    };
  }, [skipNextBlockRef]);

  useEffect(() => {
    return registerWorkflowSeedRuntime(model.workflowId, {
      hasUnsavedChanges: () => model.hasUnsavedChangesWatchable.snapshot(),
      hasDraftChangesAgainstBaseline: () =>
        model.hasDraftChangesAgainstBaseline(),
      isNew: () => model.isNew,
      persistNewWorkflow: async (params) => {
        const result = await model.persistDraft({
          name: model.getNameForSave(),
          ...(params.folderId !== undefined && { folderId: params.folderId }),
        });
        return result.success
          ? { ok: true }
          : { ok: false, message: result.message };
      },
      markPersisted: () => model.markNoLongerNew(),
    });
  }, [model]);

  const shouldBlock = React.useCallback(() => {
    if (skipNextBlockRef.current === true) {
      skipNextBlockRef.current = false;
      return false;
    }
    return model.hasUnsavedChangesWatchable.snapshot();
  }, [model, skipNextBlockRef]);

  const onSaveAndProceed = React.useCallback(
    async (nextLocation: { pathname: string }) => {
      const isNavigatingToChat = nextLocation.pathname.includes(
        `/${org.slug}/chats/`
      );
      if (!isNavigatingToChat) {
        return false;
      }
      const result = await saveWorkflowAction.execute({
        silent: true,
        workflowId: model.workflowId,
        ...(folderIdFromState !== undefined && { folderId: folderIdFromState }),
      });
      if (result.isErr()) {
        return false;
      }
      skipNextBlockRef.current = true;
      return true;
    },
    [folderIdFromState, model, org.slug, saveWorkflowAction, skipNextBlockRef]
  );

  useBlockNavigationWithModal(shouldBlock, {
    message: UNSAVED_CHANGES_MESSAGE,
    onSaveAndProceed,
    blockEnabled: hasUnsavedChanges,
  });

  useWorkflowAutosave({
    model,
    save: () =>
      void saveWorkflowAction.execute({
        silent: true,
        workflowId: model.workflowId,
        ...(folderIdFromState !== undefined && { folderId: folderIdFromState }),
      }),
  });

  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Workflow,
    resourceId: model.workflowId,
    resourceName: name,
    folderId: isPersisted ? folderId : null,
    folderScope: isPersisted ? folderScope : null,
  });

  const shellBreadcrumbs = useMemo(() => {
    if (activeRunStepView !== null) {
      return [
        {
          label: runDetailsCrumbLabel(activeRunStepView.runId),
          onClick: handleCloseStackView,
        },
        { label: 'Step details' },
      ];
    }
    if (activeRunView !== null) {
      return [
        {
          label: name ?? 'Workflow',
          onClick: handleCloseStackView,
        },
        { label: runDetailsCrumbLabel(activeRunView.runId) },
      ];
    }
    if (activeDrillView !== null) {
      return [
        {
          label: name ?? 'Workflow',
          onClick: handleCloseStackView,
        },
        { label: getWorkflowDrillTargetTitle(activeDrillView.target) },
      ];
    }
    if (activeGraphView !== null) {
      return [
        {
          label: name ?? 'Workflow',
          onClick: handleCloseStackView,
        },
        { label: activeGraphView.targetWorkflowName ?? 'Workflow' },
      ];
    }
    if (activeTreeView !== null) {
      return [
        {
          label: name ?? 'Workflow',
          onClick: handleCloseStackView,
        },
        { label: WORKFLOW_TREE_VIEW_TITLE },
      ];
    }
    return breadcrumbs;
  }, [
    activeRunView,
    activeDrillView,
    activeGraphView,
    activeTreeView,
    name,
    breadcrumbs,
    handleCloseStackView,
    activeRunStepView,
  ]);

  // The top view renders a resource page of its own, so the workflow shell keeps
  // its breadcrumbs, back button and save indicator out of the way.
  const topViewOwnsChrome = viewOwnsPageChrome(
    viewStack?.[viewStack.length - 1]?.payload
  );

  const stackBackLeading =
    canPopView && !topViewOwnsChrome ? (
      <ResourcePageShellBackButton onClick={handleCloseStackView} />
    ) : undefined;

  // The archived notice belongs to the workflow itself, so it stays off a
  // pushed view (a run, a step, a drilled node, a nested graph) — those are
  // about something else and already own their own chrome.
  const stackViewActive =
    activeAgentView !== null ||
    activeGraphView !== null ||
    activeTreeView !== null ||
    activeDatasetView !== null ||
    activeDrillView !== null ||
    activeRunStepView !== null ||
    activeRunView !== null;

  const workflowActions = (
    <div className="flex shrink-0 items-center gap-2">
      {isArchived ? (
        // Explicit icon: the `warning` variant defaults to an alert triangle,
        // which overstates it — archived is a state, not a fault.
        <Chip
          variant="warning"
          tight
          icon={<Archive className="h-3.5 w-3.5" />}
        >
          Archived
        </Chip>
      ) : null}
      <Tooltip
        side="bottom"
        tooltipContent={
          hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'
        }
      >
        <span className="shrink-0">
          {hasUnsavedChanges ? (
            <Circle className="h-2 w-2 fill-amber-500 text-amber-500" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
        </span>
      </Tooltip>
    </div>
  );

  if (loadStatus === 'loading') {
    return (
      <ResourcePageShell breadcrumbs={undefined} layout="flat" surface="editor">
        <WorkflowEditorLoading />
      </ResourcePageShell>
    );
  }

  if (loadStatus === 'not-found') {
    return (
      <ResourcePageShell
        breadcrumbs={breadcrumbs}
        layout="flat"
        surface="editor"
      >
        <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
          <Text.P className="text-muted-foreground">Workflow not found</Text.P>
        </div>
      </ResourcePageShell>
    );
  }

  // Build the canvas + runs panel content. For the "bottom" placement the
  // WorkflowEditorRunsPanel now owns a vertical ResizablePanelGroup internally,
  // so we render the canvas as a child of its first panel slot. For "side"
  // placement the layout stays as a flex-row with the runs panel beside the
  // canvas.
  const canvasContent = (
    <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden h-full">
      <WorkflowEditorFloatingChrome
        onToolbarSlotChange={handleToolbarSlotChange}
      />
      <WorkflowRenderCatalogProvider catalog={model.renderCatalog}>
        <WorkflowEditorCanvas toolbarSlot={toolbarSlot} />
      </WorkflowRenderCatalogProvider>
    </div>
  );

  const runsList = (
    <EmbeddedRunsList
      workflowId={model.workflowId}
      onClickRun={artifactPanel !== null ? handleClickRunInPanel : undefined}
      onOpenRunId={artifactPanel !== null ? openRunInPanel : undefined}
    />
  );

  return (
    <ResourcePageShell
      breadcrumbs={topViewOwnsChrome ? [] : shellBreadcrumbs}
      leading={stackBackLeading}
      // Beside the name rather than in the actions cluster; see the agent page.
      titleAdornment={
        topViewOwnsChrome ? undefined : (
          <FavoriteToggleButton
            type={ResourceType.Workflow}
            id={model.workflowId}
            className="h-6 w-6"
            iconClassName="h-3.5 w-3.5"
          />
        )
      }
      actions={topViewOwnsChrome ? undefined : workflowActions}
      layout="flat"
      surface="editor"
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        {isArchived && !stackViewActive ? (
          <ArchivedNotice>
            This workflow is archived. It no longer appears in the workflows
            list, in the Library, or under the triggers it is attached to — but
            its triggers still fire it, and those runs stay hidden with it.
          </ArchivedNotice>
        ) : null}
        {activeAgentView !== null ? (
          <WorkflowAgentStackContent view={activeAgentView} />
        ) : activeGraphView !== null ? (
          <ActiveArtifactIdOverride
            artifactId={activeGraphView.targetWorkflowId}
          >
            <WorkflowPageWithFetchedData
              id={activeGraphView.targetWorkflowId}
              stackViewsEnabled={false}
            />
          </ActiveArtifactIdOverride>
        ) : activeTreeView !== null ? (
          <WorkflowNestedTreeStackContent view={activeTreeView} />
        ) : activeDatasetView !== null ? (
          <WorkflowDatasetStackContent view={activeDatasetView} />
        ) : activeDrillView !== null ? (
          <WorkflowDrillStackContent
            view={activeDrillView}
            onClose={handleCloseStackView}
          />
        ) : activeRunStepView !== null ? (
          <EmbeddedRunStep view={activeRunStepView} />
        ) : activeRunView !== null ? (
          <WorkflowRunStackContent
            workflowId={activeRunView.workflowId}
            runId={activeRunView.runId}
            onOpenRunId={artifactPanel !== null ? openRunInPanel : undefined}
          />
        ) : !isPersisted ? (
          // A workflow that has never been saved can't have runs yet, so the
          // runs panel/bar is hidden until the first save — just the canvas.
          canvasContent
        ) : (
          // Runs always sit along the bottom: the RunsPanel owns a vertical
          // ResizablePanelGroup wrapping both the canvas (first panel) and the
          // runs list (second), so the user can drag the divider to resize.
          // Never a side column — that edge belongs to the node inspector.
          <WorkflowEditorRunsPanel
            open={runsPanelOpen}
            onOpenChange={handleRunsPanelOpenChange}
            canvasSlot={canvasContent}
          >
            {runsList}
          </WorkflowEditorRunsPanel>
        )}
      </div>
    </ResourcePageShell>
  );
};

const WorkflowPageWithModelScopeResolved: React.FC<{
  id: string;
  isNew: boolean;
  orgId: string;
  userId: string;
  folderId?: string;
  seedTrigger?: WorkflowTriggerOption;
  seedNewDatasetTrigger?: WorkflowSeedNewDatasetTrigger;
  initialEditorTab?: WorkflowEditorTab;
  stackViewsEnabled?: boolean;
}> = ({
  id,
  isNew,
  orgId,
  userId,
  folderId,
  seedTrigger,
  seedNewDatasetTrigger,
  initialEditorTab,
  stackViewsEnabled,
}) => {
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();
  const urlAdapter = useUrlAdapter();
  const skipNextBlockRef = useRef(false);
  const activeArtifactId = useActiveArtifactId();

  const createModels = useCallback(
    () => [
      new WorkflowModel({
        workflowId: id,
        orgId,
        userId,
        isNew,
        ...(folderId !== undefined && { folderId }),
        ...(seedTrigger !== undefined && { seedTrigger }),
        ...(seedNewDatasetTrigger !== undefined && { seedNewDatasetTrigger }),
        ...(initialEditorTab !== undefined && { initialEditorTab }),
        client,
        watchablesFactory,
        urlAdapter,
      }),
    ],
    [
      id,
      orgId,
      userId,
      isNew,
      folderId,
      seedTrigger,
      seedNewDatasetTrigger,
      initialEditorTab,
      client,
      watchablesFactory,
      urlAdapter,
    ]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[id, isNew, urlAdapter]}
      key={id}
    >
      <WorkflowModelStackAwareProvider
        modelId={id}
        baseTarget={activeArtifactId === null || activeArtifactId === id}
      >
        <WorkflowPageContent
          folderIdFromState={folderId}
          skipNextBlockRef={skipNextBlockRef}
          stackViewsEnabled={stackViewsEnabled}
        />
      </WorkflowModelStackAwareProvider>
    </ModelScopeFactory>
  );
};

const WorkflowPageWithModelScope: React.FC<{
  id: string;
  isNew: boolean;
  folderId?: string;
  seedTrigger?: WorkflowTriggerOption;
  seedNewDatasetTrigger?: WorkflowSeedNewDatasetTrigger;
  initialEditorTab?: WorkflowEditorTab;
  stackViewsEnabled?: boolean;
}> = ({
  id,
  isNew,
  folderId,
  seedTrigger,
  seedNewDatasetTrigger,
  initialEditorTab,
  stackViewsEnabled,
}) => {
  // Optional chaining + guard: the artifact panel can mount before whoami/org
  // context is ready, so wait for it rather than dereferencing undefined.
  const orgId = useWhoami((s) => s.org?.id);
  const userId = useWhoami((s) => s.whoami?.id);

  if (orgId === undefined || userId === undefined) {
    return (
      <ResourcePageShell breadcrumbs={undefined} layout="flat" surface="editor">
        <WorkflowEditorLoading />
      </ResourcePageShell>
    );
  }

  return (
    <WorkflowPageWithModelScopeResolved
      id={id}
      isNew={isNew}
      orgId={orgId}
      userId={userId}
      folderId={folderId}
      seedTrigger={seedTrigger}
      seedNewDatasetTrigger={seedNewDatasetTrigger}
      initialEditorTab={initialEditorTab}
      stackViewsEnabled={stackViewsEnabled}
    />
  );
};

/**
 * Workflow builder that fetches by id, for embedding in the Coco chat artifact
 * panel. Mirrors `AgentPageWithFetchedData`.
 */
export const WorkflowPageWithFetchedData: React.FC<{
  id: string;
  initialEditorTab?: WorkflowEditorTab;
  stackViewsEnabled?: boolean;
}> = ({ id, initialEditorTab, stackViewsEnabled }) => {
  return (
    <WorkflowPageWithModelScope
      id={id}
      isNew={false}
      initialEditorTab={initialEditorTab}
      stackViewsEnabled={stackViewsEnabled}
    />
  );
};

export const WorkflowArtifactPage: React.FC<{
  id: string;
  isNew?: boolean;
  folderId?: string;
  seedTrigger?: WorkflowTriggerOption;
  seedNewDatasetTrigger?: WorkflowSeedNewDatasetTrigger;
  initialEditorTab?: WorkflowEditorTab;
}> = ({
  id,
  isNew = false,
  folderId,
  seedTrigger,
  seedNewDatasetTrigger,
  initialEditorTab,
}) => {
  return (
    <Suspense fallback={null}>
      <WorkflowPageWithModelScope
        id={id}
        isNew={isNew}
        folderId={folderId}
        seedTrigger={seedTrigger}
        seedNewDatasetTrigger={seedNewDatasetTrigger}
        initialEditorTab={initialEditorTab}
      />
    </Suspense>
  );
};

const WorkflowPageSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="px-4 py-2 border-b border-slate-200/50 shrink-0 flex items-center gap-2">
      <div className="h-6 w-48 rounded bg-coco-accent animate-pulse" />
      <div className="h-4 w-4 rounded bg-coco-accent animate-pulse shrink-0" />
    </div>
    <div className="flex-1 min-h-0 overflow-hidden p-5">
      <div className="h-full w-full rounded bg-coco-accent animate-pulse" />
    </div>
  </div>
);

/**
 * Legacy `/workflows/:id` route — redirects into the chat seed flow so workflow
 * editing happens in the chat artifact panel instead of a standalone page.
 */
export const WorkflowPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useTenantedClient();
  const orgSlug = useWhoami((s) => s.org?.slug);
  const locationState = location.state as WorkflowPageLocationState;
  const isNew = locationState?.new === true;

  useTracking(CocoEvent.WorkflowViewed, {
    workflowId: workflowId ?? null,
    isNew,
  });

  useEffect(() => {
    if (workflowId === undefined || orgSlug === undefined) {
      return;
    }
    const abortController = new AbortController();
    void openArtifactChatSeed({
      client: client.resource,
      navigate,
      orgSlug,
      strategy: workflowChatSeedStrategy,
      artifactId: workflowId,
      isNew,
      folderId: locationState?.folderId,
      replace: true,
      abortSignal: abortController.signal,
    });
    return () => {
      abortController.abort();
    };
  }, [
    workflowId,
    navigate,
    orgSlug,
    client.resource,
    isNew,
    locationState?.folderId,
  ]);

  if (workflowId === undefined || workflowId === '') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Invalid workflow</Text.P>
      </div>
    );
  }

  return <WorkflowPageSkeleton />;
};
